import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import { useAuth } from "./contexts/AuthContext";
import { GoogleAnalyticsTracker } from "./hooks/useGoogleAnalytics";
import ChatAssistant from "./components/ChatAssistant";
import AboutExpertiseTrustBuilding from './pages/about-expertise-trust-building';
import ROICalculatorSuite from './pages/roi-calculator-suite-value-demonstration-tools';
import DataMaturityCalculator from './pages/data-maturity-calculator-lead-generation-assessment';
import KnowledgeHubThoughtLeadershipCenter from './pages/knowledge-hub-thought-leadership-center';
import ContactMultiChannelEngagementHub from './pages/contact-multi-channel-engagement-hub';
import ServicesOverview from './pages/services-overview-interactive-capability-showcase';
import FractionalCDOServices from './pages/fractional-cdo-services-executive-leadership-solutions';
import AISolutionsPage from './pages/ai-solutions-intelligent-data-strategy-services';
import NewsletterSubscription from './pages/newsletter-subscription-lead-nurture-center-gdpr-compliant-email-marketing-hub';
import PrivacyPolicyPage from './pages/privacy-policy-cookie-consent-management-gdpr-compliance-center';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AvailabilityManagement from './pages/AvailabilityManagement';
import CalendarDashboard from './pages/CalendarDashboard';
import ConsultationManagementConsole from './pages/consultation-management-console-administrative-dashboard';
import WorkflowAutomation from './pages/WorkflowAutomation';
import RealTimeAnalyticsDashboard from './pages/real-time-analytics-dashboard-business-intelligence-center';
import ClientPortalDashboard from './pages/client-portal-dashboard-personalized-analytics-hub';
import AssessmentHistoryAnalytics from './pages/assessment-history-analytics-progress-tracking-center';
import PersonalizedActionCenter from './pages/personalized-action-center-strategic-recommendations-hub';
import AIPoweredServiceRecommendations from './pages/ai-powered-service-recommendations-intelligent-roi-prediction-center';
import ConsultantNotifications from './pages/ConsultantNotifications';
import MondaySlotManagementConsole from './pages/monday-slot-management-console-administrative-time-slot-control-center';
import FrequentlyAskedQuestions from './pages/frequently-asked-questions-comprehensive-faq-center';
import PostBookingConfirmation from './pages/post-booking-confirmation-preparation-center-consultation-success-hub';
import FreeAssessmentsHub from './pages/free-assessments-hub';
import EmailDeliveryMonitoringDashboard from './pages/email-delivery-monitoring-dashboard-resend-integration-command-center';
import EmailTestPage from './pages/EmailTestPage';
import ScheduleConsultation from './pages/schedule-consultation';
import RequestProposal from './pages/request-proposal';
import ChatAnalyticsDashboard from './pages/chat-analytics-dashboard-monitoring-center';
import ArticleAIReadiness from './pages/knowledge-hub-thought-leadership-center/ArticleAIReadiness';
import ArticleDetail from './pages/knowledge-hub-thought-leadership-center/ArticleDetail';
import KnowledgeHubAdmin from './pages/knowledge-hub-admin';
import DataGovernanceDiagnostic from './pages/data-governance-diagnostic-assessment';
import CreateArticle from './pages/knowledge-hub-create-article';
import EditArticle from './pages/knowledge-hub-edit-article';
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  return user ? children : <Navigate to="/signin" replace />;
};
const Routes = () => {
  return (
    <BrowserRouter>
      <GoogleAnalyticsTracker />
      <ErrorBoundary>
      <ScrollToTop />
      <ChatAssistant />
      <RouterRoutes>
        {/* Public Routes */}
        <Route path="/" element={<AboutExpertiseTrustBuilding />} />
        <Route path="/about-expertise-trust-building" element={<AboutExpertiseTrustBuilding />} />
        <Route path="/roi-calculator-suite-value-demonstration-tools" element={<ROICalculatorSuite />} />
        <Route path="/data-maturity-calculator-lead-generation-assessment" element={<DataMaturityCalculator />} />
        <Route path="/knowledge-hub-thought-leadership-center" element={<KnowledgeHubThoughtLeadershipCenter />} />
        <Route path="/article/ai-readiness" element={<ArticleAIReadiness />} />
        <Route path="/article/:id" element={<ArticleDetail />} />
        <Route path="/contact-multi-channel-engagement-hub" element={<ContactMultiChannelEngagementHub />} />
        <Route path="/services-overview-interactive-capability-showcase" element={<ServicesOverview />} />
        <Route path="/fractional-cdo-services-executive-leadership-solutions" element={<FractionalCDOServices />} />
        <Route path="/ai-solutions-intelligent-data-strategy-services" element={<AISolutionsPage />} />
        <Route path="/newsletter-subscription-lead-nurture-center-gdpr-compliant-email-marketing-hub" element={<NewsletterSubscription />} />
        <Route path="/privacy-policy-cookie-consent-management-gdpr-compliance-center" element={<PrivacyPolicyPage />} />
        <Route path="/frequently-asked-questions-comprehensive-faq-center" element={<FrequentlyAskedQuestions />} />
        <Route path="/post-booking-confirmation-preparation-center-consultation-success-hub" element={<PostBookingConfirmation />} />
        <Route path="/free-assessments-hub" element={<FreeAssessmentsHub />} />
        <Route path="/data-governance-diagnostic-assessment" element={<DataGovernanceDiagnostic />} />
        <Route path="/schedule-consultation" element={<ScheduleConsultation />} />
        <Route path="/request-proposal" element={<RequestProposal />} />
        <Route path="/email-delivery-monitoring-dashboard-resend-integration-command-center" element={<EmailDeliveryMonitoringDashboard />} />
        <Route path="/email-test" element={<EmailTestPage />} />
        {/* Auth Routes */}
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/availability-management" 
          element={
            <ProtectedRoute>
              <AvailabilityManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/calendar-dashboard" 
          element={
            <ProtectedRoute>
              <CalendarDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/consultation-management-console-administrative-dashboard" 
          element={
            <ProtectedRoute>
              <ConsultationManagementConsole />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/workflow-automation" 
          element={
            <ProtectedRoute>
              <WorkflowAutomation />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/real-time-analytics-dashboard-business-intelligence-center" 
          element={
            <ProtectedRoute>
              <RealTimeAnalyticsDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/client-portal-dashboard-personalized-analytics-hub" 
          element={
            <ProtectedRoute>
              <ClientPortalDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/assessment-history-analytics-progress-tracking-center" 
          element={
            <ProtectedRoute>
              <AssessmentHistoryAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/personalized-action-center-strategic-recommendations-hub" 
          element={
            <ProtectedRoute>
              <PersonalizedActionCenter />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ai-powered-service-recommendations-intelligent-roi-prediction-center" 
          element={
            <ProtectedRoute>
              <AIPoweredServiceRecommendations />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/consultant-notifications" 
          element={
            <ProtectedRoute>
              <ConsultantNotifications />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/monday-slot-management-console-administrative-time-slot-control-center" 
          element={
            <ProtectedRoute>
              <MondaySlotManagementConsole />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/chat-analytics-dashboard-monitoring-center" 
          element={
            <ProtectedRoute>
              <ChatAnalyticsDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-hub-admin" 
          element={
            <ProtectedRoute>
              <KnowledgeHubAdmin />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-hub-admin/create-article" 
          element={
            <ProtectedRoute>
              <CreateArticle />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/knowledge-hub-admin/edit-article/:id" 
          element={
            <ProtectedRoute>
              <EditArticle />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};
export default Routes;